import utilSvc from '../../utilSvc';
import { formatLocalDate } from '../../components/validation/ValidInputDate.utils';
import { calendarDayDiff } from './calendarDayDiff';

export type EndChangeEffect = {
    /** New endError value to set, or null to leave unchanged. */
    endError: string | null;
    /** New startError value to set, or null to leave unchanged. */
    startError: string | null;
    /** Value for setProperty('StartDate', …), or null to skip. */
    startDateWrite: string | null;
    /** Value for setProperty('EndDate', …). Defined when earlyReturn is false. */
    endDateWrite: string | null;
    /** When true: apply batchWrite then return — skip start revalidation and EndDate write. */
    earlyReturn: boolean;
    /** Date writes for batchSet when earlyReturn is true. */
    batchWrite: Record<string, string> | null;
};

// ---------------------------------------------------------------------------
// Pure computation for handleEndChange decision logic.
// All inputs are zeroTime-normalised dates. Returns an effect descriptor with
// no side effects — the hook applies the effect.
// ---------------------------------------------------------------------------
export function computeEndChangeEffect(
    s: Date,
    e: Date,
    today: Date,
    maxDays: number,
    currentEndError: string,
    currentStartError: string,
): EndChangeEffect {
    const clampedStart = utilSvc.dt.addDays(e, -(maxDays - 1));
    const diffDays = calendarDayDiff(s, e);

    // Main end-error branch — mirrors the if-else chain in handleEndChange
    if (e < s) {
        return {
            endError: 'End date must not be before start date.',
            startError: 'Start date must not be after end date.',
            startDateWrite: null,
            endDateWrite: formatLocalDate(e),
            earlyReturn: false,
            batchWrite: null,
        };
    } else if (s > today) {
        return {
            endError: 'Start date cannot be in the future.',
            startError: 'Start date cannot be in the future.',
            startDateWrite: null,
            endDateWrite: formatLocalDate(e),
            earlyReturn: false,
            batchWrite: null,
        };
    } else if (e > today) {
        return {
            endError: 'End date cannot be in the future.',
            startError: currentStartError ? '' : null,
            startDateWrite: formatLocalDate(clampedStart),
            endDateWrite: formatLocalDate(e),
            earlyReturn: false,
            batchWrite: null,
        };
    } else if (currentEndError) {
        return {
            endError: '',
            startError: currentStartError ? '' : null,
            startDateWrite: null,
            endDateWrite: formatLocalDate(e),
            earlyReturn: false,
            batchWrite: null,
        };
    } else if (diffDays > maxDays - 1) {
        return {
            endError: 'Date range cannot exceed ' + maxDays + ' days.',
            startError: null,
            startDateWrite: null,
            endDateWrite: null,
            earlyReturn: true,
            batchWrite: { StartDate: formatLocalDate(clampedStart), EndDate: formatLocalDate(e) },
        };
    }

    // Happy path: valid range, no errors
    return {
        endError: null,
        startError: null,
        startDateWrite: null,
        endDateWrite: formatLocalDate(e),
        earlyReturn: false,
        batchWrite: null,
    };
}
